// ============================================================
//  Legal content for Privacy Policy and Accessibility Statement
//  Used by Footer.tsx modals — edit here to update legal text.
// ============================================================

export interface LegalSection {
  heading?: string
  body: string
  isContact?: boolean
}

export interface LegalDocument {
  effectiveDate?: string
  lastUpdated?: string
  intro: string
  sections: LegalSection[]
}

// ─── Privacy Policy ──────────────────────────────────────────

export const privacyPolicyEN: LegalDocument = {
  effectiveDate: 'March 1, 2026',
  lastUpdated: 'March 25, 2026',
  intro:
    'The Honduran-American Association ("HAA," "the Association," "we," "us," or "our") respects your privacy and is committed to handling personal information responsibly, transparently, and in accordance with applicable law.',
  sections: [
    {
      heading: '1. Scope of This Privacy Policy',
      body: 'This Privacy Policy applies to personal information we collect online and offline in connection with our website, chatbot, forms, communications, directory features, and other services or outreach activities.',
    },
    {
      heading: '2. Information We Collect',
      body: 'A. Information You Provide Directly: Name, email address, phone number, mailing address, organization name, messages, event registration details, and volunteer or business inquiry information.\n\nB. Chatbot and Messaging Information: Name, phone number, message contents, conversation history, timestamps, session details, and technical information for performance improvement.\n\nC. Information Collected Automatically: IP address, browser type, operating system, device type, pages visited, links clicked, usage patterns, and cookie data.',
    },
    {
      heading: '3. Categories of Sensitive Information',
      body: 'Please do not send sensitive personal information unless specifically requested and necessary.',
    },
    {
      heading: '4. How We Use Information',
      body: 'We use information to respond to inquiries, provide community resources, operate and improve services, enhance chatbot performance, communicate updates, manage programs, detect fraud, and comply with legal obligations.',
    },
    {
      heading: '5. Legal Bases and Consent',
      body: 'By using our services or providing information, you consent to the use as described. We will obtain explicit consent when required.',
    },
    {
      heading: '6. Cookies and Tracking',
      body: 'We may use cookies and analytics tools to improve performance and user experience. You can control cookies through your browser settings.',
    },
    {
      heading: '7. Chatbot, AI, and Automated Interaction Notice',
      body: 'Our website may use chatbot or AI tools. Conversations may be recorded and reviewed. Responses may be inaccurate. The chatbot is for informational purposes only and does not provide legal, immigration, financial, or professional advice. Do not submit sensitive or confidential information.',
    },
    {
      heading: '8. How We Disclose Information',
      body: 'We do not sell personal information. We may share data with service providers (hosting, chatbot, analytics, messaging), legal authorities if required, security entities, and organizational partners when necessary.',
    },
    {
      heading: '9. No Sale of Personal Information',
      body: 'We do not sell or rent personal information.',
    },
    {
      heading: '10. Business Directory & Third-Party Listings Disclaimer',
      body: 'Our website may include listings of businesses and organizations. Information may come from public or third-party sources. Accuracy is not guaranteed. Listings are not endorsements or certifications. Users must verify information independently. We are not liable for damages, disputes, or losses related to listings.',
    },
    {
      heading: '11. Third-Party Websites',
      body: 'We are not responsible for third-party sites or services linked on our website.',
    },
    {
      heading: '12. Data Retention',
      body: 'We retain data for the necessary period for operations, legal compliance, and service improvement.',
    },
    {
      heading: '13. Data Security',
      body: 'We use reasonable safeguards, but no system is fully secure. Use caution when sharing information.',
    },
    {
      heading: "14. Children's Privacy",
      body: 'We do not knowingly collect data from children under 13.',
    },
    {
      heading: '15. Your Privacy Rights',
      body: 'Depending on your location, you may have rights to access, correct, delete your data, or opt out of certain uses. Requests may be subject to verification and legal limitations.',
    },
    {
      heading: '16. How to Exercise Your Rights',
      body: 'To submit a privacy request, contact us at: info@honduranamerican.org. Include sufficient details to process your request. We may verify your identity before responding.',
    },
    {
      heading: '17. Do Not Track',
      body: 'We may not respond to all "Do Not Track" signals from your browser, except as required by law.',
    },
    {
      heading: '18. Data Transfers',
      body: 'Your information may be processed in the United States or other jurisdictions.',
    },
    {
      heading: '19. Changes to This Policy',
      body: 'We may update this policy at any time. Continued use of our services indicates acceptance of updates.',
    },
    {
      heading: '20. Contact Us',
      body: 'Honduran-American Association\nWebsite: honduranamerican.org\nEmail: info@honduranamerican.org',
      isContact: true,
    },
    {
      heading: 'Chatbot Privacy Notice',
      body: 'This chatbot is provided solely for general informational purposes. Conversations may be recorded, stored, and reviewed to improve quality, accuracy, security, and performance. Do not send sensitive personal, financial, medical, or other confidential information through this chatbot.',
    },
    {
      heading: 'Directory Disclaimer',
      body: 'Business and community listings are provided solely for informational purposes. Information may come from public or third-party sources and may change without prior notice. The Honduran-American Association does not verify, endorse, certify, or guarantee any businesses, organizations, addresses, or services listed.',
    },
  ],
}

export const privacyPolicyES: LegalDocument = {
  effectiveDate: '1 de marzo de 2026',
  lastUpdated: '25 de marzo de 2026',
  intro:
    'La Asociación Hondureño-Americana ("HAA," "la Asociación," "nosotros" o "nuestro") respeta su privacidad y se compromete a manejar la información personal de manera responsable, transparente y de acuerdo con la ley aplicable.',
  sections: [
    {
      heading: '1. Alcance',
      body: 'Esta política se aplica a la información que recopilamos en línea y fuera de línea a través de nuestro sitio web, chatbot, formularios, comunicaciones, directorios y servicios relacionados.',
    },
    {
      heading: '2. Información Que Recopilamos',
      body: 'A. Información Que Usted Proporciona: Nombre, correo electrónico, teléfono, dirección, organización, mensajes y detalles de eventos o suscripciones.\n\nB. Chatbot y Mensajería: Nombre, teléfono, contenido de mensajes, historial de conversaciones, metadatos y datos técnicos para mejorar el rendimiento.\n\nC. Información Automática: Dirección IP, tipo de navegador, sistema operativo, páginas visitadas, cookies y patrones de uso.',
    },
    {
      heading: '3. Información Sensible',
      body: 'No envíe información sensible a menos que sea específicamente solicitada y necesaria.',
    },
    {
      heading: '4. Cómo Usamos la Información',
      body: 'Usamos la información para responder consultas, proporcionar recursos, mejorar servicios, comunicar actualizaciones, gestionar programas, detectar fraude y cumplir con obligaciones legales.',
    },
    {
      heading: '5. Bases Legales y Consentimiento',
      body: 'Al usar nuestros servicios o proporcionar información, usted consiente su uso según lo descrito. Cuando sea requerido, obtendremos consentimiento explícito.',
    },
    {
      heading: '6. Cookies y Seguimiento',
      body: 'Podemos usar cookies y herramientas de análisis. Puede controlar las cookies a través de la configuración de su navegador.',
    },
    {
      heading: '7. Aviso de Chatbot e IA',
      body: 'Las conversaciones pueden ser grabadas y revisadas. El chatbot es solo para fines informativos y no proporciona asesoramiento legal, de inmigración o profesional. No envíe información confidencial.',
    },
    {
      heading: '8. Divulgación de Información',
      body: 'No vendemos información personal. Podemos compartir datos con proveedores de servicios, autoridades legales y socios organizacionales cuando sea necesario.',
    },
    {
      heading: '9. No Venta de Información Personal',
      body: 'No vendemos ni alquilamos información personal.',
    },
    {
      heading: '10. Directorio de Negocios',
      body: 'Los listados son solo informativos. La información puede provenir de fuentes públicas. No se garantiza la precisión. Los listados no son respaldos. No somos responsables de daños relacionados con los listados.',
    },
    {
      heading: '11. Sitios Web de Terceros',
      body: 'No somos responsables de sitios o servicios de terceros vinculados en nuestro sitio web.',
    },
    {
      heading: '12. Retención de Datos',
      body: 'Retenemos datos el tiempo necesario para operaciones, cumplimiento legal y mejora del servicio.',
    },
    {
      heading: '13. Seguridad de Datos',
      body: 'Usamos salvaguardas razonables, pero ningún sistema es completamente seguro.',
    },
    {
      heading: '14. Privacidad de Niños',
      body: 'No recopilamos datos de niños menores de 13 años a sabiendas.',
    },
    {
      heading: '15. Sus Derechos de Privacidad',
      body: 'Dependiendo de su ubicación, puede tener derechos para acceder, corregir, eliminar o optar por no usar ciertos usos de sus datos.',
    },
    {
      heading: '16. Cómo Ejercer Sus Derechos',
      body: 'Para enviar una solicitud de privacidad, contáctenos en: info@honduranamerican.org. Incluya suficientes detalles para procesar su solicitud. Podemos verificar su identidad antes de responder.',
    },
    {
      heading: '17. No Rastrear',
      body: 'Es posible que no respondamos a todas las señales "No Rastrear" del navegador, excepto según lo requiera la ley.',
    },
    {
      heading: '18. Transferencias de Datos',
      body: 'Su información puede ser procesada en los Estados Unidos u otras jurisdicciones.',
    },
    {
      heading: '19. Cambios a Esta Política',
      body: 'Podemos actualizar esta política en cualquier momento. El uso continuado indica aceptación.',
    },
    {
      heading: '20. Contáctenos',
      body: 'Asociación Hondureño-Americana\nSitio web: honduranamerican.org\nCorreo: info@honduranamerican.org',
      isContact: true,
    },
    {
      heading: 'Aviso de Privacidad del Chatbot',
      body: 'Este chatbot se proporciona solo con fines informativos generales. Las conversaciones pueden ser grabadas, almacenadas y revisadas para mejorar la calidad, precisión, seguridad y rendimiento del servicio. No envíe información personal sensible, financiera, médica u otra información confidencial a través de este chatbot.',
    },
    {
      heading: 'Descargo de Responsabilidad del Directorio',
      body: 'Los listados de negocios y comunidades se proporcionan solo con fines informativos. La información puede provenir de fuentes públicas o de terceros y puede cambiar sin previo aviso. La Asociación Hondureño-Americana no verifica, respalda, certifica ni garantiza los negocios, organizaciones, direcciones o servicios listados.',
    },
  ],
}

// ─── Accessibility Statement ─────────────────────────────────

export const accessibilityEN: string[] = [
  'The Honduran-American Association is committed to making this website accessible and usable for as many people as possible, including people with disabilities. We believe everyone should be able to access information about our mission, programs, and resources with dignity and ease.',
  'We are continually working to improve the accessibility and usability of this site and to follow recognized best practices for digital accessibility. As part of that commitment, we aim to design content that is clear, readable, and accessible across devices and assistive technologies.',
  'Despite our efforts, some areas of the website may not yet be fully accessible or may still be in the process of improvement. We welcome feedback from visitors who encounter barriers while using our site.',
]

export const accessibilityES: string[] = [
  'La Asociación Hondureño-Americana se compromete a hacer que este sitio web sea accesible y utilizable para la mayor cantidad de personas posible, incluidas las personas con discapacidades. Creemos que todos deberían poder acceder a la información sobre nuestra misión, programas y recursos con dignidad y facilidad.',
  'Trabajamos continuamente para mejorar la accesibilidad y usabilidad de este sitio y seguir las mejores prácticas reconocidas para la accesibilidad digital. Como parte de ese compromiso, nos esforzamos por diseñar contenido que sea claro, legible y accesible en todos los dispositivos y tecnologías de asistencia.',
  'A pesar de nuestros esfuerzos, algunas áreas del sitio web pueden no ser completamente accesibles o pueden estar aún en proceso de mejora. Agradecemos los comentarios de los visitantes que encuentren barreras al usar nuestro sitio.',
]

export const accessibilityContactEN =
  'If you experience difficulty accessing any part of this website, notice an accessibility issue, or would like to request assistance, please contact us at'
export const accessibilityContactES =
  'Si tiene dificultades para acceder a cualquier parte de este sitio web, nota un problema de accesibilidad o desea solicitar asistencia, contáctenos en'
export const accessibilityContactSuffixEN =
  '. We will do our best to respond promptly and provide the information or support you need.'
export const accessibilityContactSuffixES =
  '. Haremos todo lo posible para responder con prontitud y brindarle la información o el apoyo que necesite.'
